import { Warning } from '@mui/icons-material'
import { Box, Grid, Skeleton, Typography, useMediaQuery, useTheme } from '@mui/material'

import useDates from '../../../../../utils/hooks/useDates'
import usePauseDevice from '../../../../../utils/hooks/usePauseDevice'
import useTemperatures from '../../../../../utils/hooks/useTemperatures'
import classes from '../../index.styles'

import StatusSectionItem from './status-section-item'

const StatusSection = ({ device }: { device: IDeviceType | null }) => {
  const theme = useTheme()
  const isLargeScreen = useMediaQuery(theme.breakpoints.up('lg'))
  const isXLargeScreen = useMediaQuery(theme.breakpoints.up('xl'))
  const nodeTypeName = device?.node.nodeTypeName || ''
  const { formatTemperature } = useTemperatures()
  const { formatDateTimeShortMonth } = useDates()
  const { isPaused, pausedText } = usePauseDevice({ device: device || undefined })
  const activeAlerts = device?.alerts
    ? device.alerts.filter((alert) => alert.status === 'active' && (alert.tags.includes('urgent') || alert.tags.includes('warning')))
    : []
  const isBVS = nodeTypeName === 'bvs' || nodeTypeName === 'bvs2'

  const {
    signal_indicator: signalIndicator,
    battery_indicator: batteryIndicator,
    temp,
    leak,
    probe,
    probe_temp: probeTemp,
    probetype,
    probe_detected: probeDetected,
    hum,
    valve,
    leak_probe: leakProbe,
    nh3,
    warning_nh3: warningNh3,
    powersource,
    defroute,
    water_pressure: waterPressure,
    temp_unit: tempUnit,
  } = device?.currentValues || {}

  let tempLabel = 'Temp'
  if (isBVS) tempLabel = 'Ambient Temp'
  if (nodeTypeName === 'aqua_scope_monitor_ps') tempLabel = 'Water Temp'

  let tempChannel = temp
  if (tempChannel && typeof tempChannel.value === 'number') {
    if (isBVS) {
      tempChannel = { ...temp, value: formatTemperature(temp.value as number, 'integer', tempUnit?.value !== 'fahrenheit' ? 'degC' : 'degF') }
    } else {
      tempChannel = { ...temp, value: formatTemperature(temp.value as number, 'integer') }
    }
  }

  let probeChannel = probe
  if (probeChannel && typeof probeChannel.value === 'number') {
    probeChannel = { ...probe, value: formatTemperature(probe.value as number, 'integer') }
  }

  let probeTempChannel = probeTemp
  if (probeTempChannel && typeof probeTempChannel.value === 'number') {
    probeTempChannel = { ...probeTemp, value: formatTemperature(probeTemp.value as number, 'integer') }
  }

  let humChannel = hum
  if (humChannel && typeof humChannel.value === 'number') {
    humChannel = { ...hum, value: `${Math.round(Number(hum.value))}%` }
  }

  const renderedChannels: Array<{ channel: IChannelValueType | undefined; channelName: string }> = [
    { channel: tempChannel, channelName: 'temp' },
    { channel: leak, channelName: 'leak' },
    { channel: leakProbe, channelName: 'leak_probe' },
    { channel: signalIndicator, channelName: 'signal_indicator' },
    { channel: batteryIndicator, channelName: 'battery_indicator' },
    { channel: probeTempChannel, channelName: 'probe_temp' },
    { channel: humChannel, channelName: 'hum' },
    { channel: powersource, channelName: 'powersource' },
    { channel: defroute, channelName: 'defroute' },
    { channel: valve, channelName: 'valve' },
    { channel: nh3, channelName: 'nh3' },
    { channel: warningNh3, channelName: 'warning_nh3' },
  ]

  const shouldShowChannel = (channel: IChannelValueType | undefined, channelName: string) => {
    // do not show the channel if it doesn't exist, if it doesn't have a value, or if it doesn't have a timestamp
    if (!channel || !channel.value) return false
    if (['temp', 'probe_temp', 'probe', 'hum'].includes(channelName) && !new Date(channel.timestamp).valueOf()) return false
    // only show the defroute if plugged into ethernet, otherwise the signal will show
    if (channelName === 'defroute' && channel.value !== 'eth0') return false
    // do not show the signal if plugged into ethernet
    if (channelName === 'signal_indicator' && defroute?.value === 'eth0') return false
    // do not show power source or temp for bvs
    if ((channelName === 'powersource' || channelName === 'temp') && isBVS) return false
    // do not show battery if device is a gateway and powersource is not BATT
    if (channelName === 'battery_indicator' && nodeTypeName === 'gateway' && powersource?.value !== 'BATT') return false
    // do not show probe channels if probetype is not set to the associated type
    if (channelName === 'probe_temp' && probetype?.value !== 'temp') return false
    if (channelName === 'leak_probe' && probetype?.value !== 'leak') return false
    if (channelName === 'leak_probe' && String(probeDetected?.value) !== 'true') return false
    if (channelName === 'probe_temp' && String(probeDetected?.value) !== 'true') return false
    if (channelName === 'leak' && String(probeDetected?.value) === 'true' && probetype?.value === 'leak') return false

    return true
  }

  const visibleCount = renderedChannels.filter(({ channel, channelName }) => shouldShowChannel(channel, channelName)).length
  const wrapThreshold = isXLargeScreen ? 6 : 5
  const isWrapping = visibleCount > wrapThreshold
  const lgCols = isWrapping ? Math.ceil(visibleCount / 2) : visibleCount

  return (
    <Grid container={true} size={12} sx={classes.sectionOuterGrid}>
      <Grid container={true} size={12} sx={classes.sectionTitleGrid}>
        <Typography variant="subtitle1" sx={classes.sectionTitle}>
          {device ? 'Status' : <Skeleton width={60} />}
        </Typography>
        {device ? <Typography variant="body2">Readings as of {formatDateTimeShortMonth(device.lastCheckIn, true)}:</Typography> : <Skeleton />}
        {device && device.isOffline && (
          <Grid container={true} sx={classes.statusSectionOfflineNoticeGrid}>
            <Warning sx={classes.statusSectionOfflineNoticeIcon} />
            <Typography variant="overline" sx={classes.statusSectionOfflineNoticeText}>
              Device is no longer checking in
            </Typography>
          </Grid>
        )}
      </Grid>

      {isPaused && (
        <Grid container={true} size={12} sx={classes.statusPaused(theme, isLargeScreen)}>
          <Typography variant="body1" sx={classes.statusPausedText}>
            {pausedText}
          </Typography>
          <Typography variant="body2" sx={classes.statusPausedText}>
            No alerts are being received.
          </Typography>
        </Grid>
      )}

      {device ? (
        <Box sx={classes.statusSectionItemsGrid(isLargeScreen, lgCols, isWrapping, device.isOffline)}>
          {shouldShowChannel(tempChannel, 'temp') && (
            <StatusSectionItem
              label={tempLabel}
              channel={tempChannel}
              channelName="temp"
              nodeTypeName={nodeTypeName}
              isWrapping={isWrapping}
              activeAlerts={activeAlerts}
            />
          )}
          {shouldShowChannel(leak, 'leak') && (
            <StatusSectionItem
              label="Water"
              channel={leak}
              channelName="leak"
              nodeTypeName={nodeTypeName}
              isWrapping={isWrapping}
              activeAlerts={activeAlerts}
            />
          )}
          {shouldShowChannel(leakProbe, 'leak_probe') && (
            <StatusSectionItem
              label="Leak Probe"
              channel={leakProbe}
              channelName="leak_probe"
              nodeTypeName={nodeTypeName}
              isWrapping={isWrapping}
              activeAlerts={activeAlerts}
            />
          )}
          {shouldShowChannel(signalIndicator, 'signal_indicator') && (
            <StatusSectionItem
              label="Signal"
              channel={signalIndicator}
              channelName="signal_indicator"
              nodeTypeName={nodeTypeName}
              isWrapping={isWrapping}
              activeAlerts={activeAlerts}
            />
          )}
          {shouldShowChannel(batteryIndicator, 'battery_indicator') && (
            <StatusSectionItem
              label="Battery"
              channel={batteryIndicator}
              channelName="battery_indicator"
              nodeTypeName={nodeTypeName}
              isWrapping={isWrapping}
              activeAlerts={activeAlerts}
            />
          )}
          {shouldShowChannel(probeTempChannel, 'probe_temp') && (
            <StatusSectionItem
              label="Probe Temp"
              channel={probeTempChannel}
              channelName="probe_temp"
              nodeTypeName={nodeTypeName}
              isWrapping={isWrapping}
              activeAlerts={activeAlerts}
            />
          )}
          {shouldShowChannel(humChannel, 'hum') && (
            <StatusSectionItem
              label="Humidity"
              channel={humChannel}
              channelName="hum"
              nodeTypeName={nodeTypeName}
              isWrapping={isWrapping}
              activeAlerts={activeAlerts}
            />
          )}
          {shouldShowChannel(powersource, 'powersource') && (
            <StatusSectionItem
              label="Power source"
              channel={powersource}
              channelName="powersource"
              nodeTypeName={nodeTypeName}
              isWrapping={isWrapping}
              activeAlerts={activeAlerts}
            />
          )}
          {shouldShowChannel(defroute, 'defroute') && (
            <StatusSectionItem
              label="Connection type"
              channel={defroute}
              channelName="defroute"
              nodeTypeName={nodeTypeName}
              isWrapping={isWrapping}
              activeAlerts={activeAlerts}
            />
          )}
          {shouldShowChannel(valve, 'valve') && (
            <StatusSectionItem
              label="Valve"
              channel={valve}
              channelName="valve"
              nodeTypeName={nodeTypeName}
              isWrapping={isWrapping}
              activeAlerts={activeAlerts}
            />
          )}
          {shouldShowChannel(nh3, 'nh3') && (
            <StatusSectionItem
              label="Amm. Level"
              channel={nh3}
              channelName="nh3"
              nodeTypeName={nodeTypeName}
              isWrapping={isWrapping}
              activeAlerts={activeAlerts}
            />
          )}
          {shouldShowChannel(warningNh3, 'warning_nh3') && (
            <StatusSectionItem
              label="Amm. Warning"
              channel={warningNh3}
              channelName="warning_nh3"
              nodeTypeName={nodeTypeName}
              isWrapping={isWrapping}
              activeAlerts={activeAlerts}
            />
          )}
          {shouldShowChannel(waterPressure, 'water_pressure') && (
            <StatusSectionItem
              label="Pressure"
              channel={waterPressure}
              channelName="water_pressure"
              nodeTypeName={nodeTypeName}
              isWrapping={isWrapping}
              activeAlerts={activeAlerts}
            />
          )}
        </Box>
      ) : (
        <Skeleton variant="rounded" width="100%" height={64} />
      )}
    </Grid>
  )
}

export default StatusSection
